/**
 * UPT-2124 J4 (spec `web/SPEC-UPT-2124-J4.md` §2 n° 2) — les DÉFINITIONS des huit outils d'interface, telles que le modèle vocal les reçoit par le
 * `session.update` de l'ouverture du canal (J2a). Les outils métier d'une page (`next_step`, `create_dossier`, outils de l'Idée) priment : la consigne du
 * modèle les cite d'abord ; ceux-ci ne servent qu'en l'absence d'outil adapté.
 */
import type { VoiceToolDefinition } from '../voiceTypes';

/** Noms des outils (le registre, la consigne et le simulateur les partagent). */
export const UI_TOOL_NAMES = ['ui_read', 'ui_navigate', 'ui_click', 'ui_fill', 'ui_select', 'ui_scroll', 'ui_back', 'ui_undo'] as const;
export type UiToolName = (typeof UI_TOOL_NAMES)[number];

const object = (properties: Record<string, unknown>, required: string[]): Record<string, unknown> => ({
  type: 'object',
  properties,
  required,
  additionalProperties: false,
});

const CLICK_ROLE_ENUM = ['button', 'link', 'tab', 'menuitem', 'row', 'option', 'checkbox', 'radio', 'switch'];

export const UI_TOOL_DEFINITIONS: Readonly<Record<UiToolName, VoiceToolDefinition>> = {
  ui_read: {
    type: 'function',
    name: 'ui_read',
    description:
      "Lit ce qui est à l'écran : titre, onglets, champs avec leur valeur, boutons ([confirm] = demande une confirmation, [screen only] = se fait à l'écran), lignes de liste. À appeler d'abord quand tu ne sais pas ce qui est affiché. Les données masquées ne sont jamais lues.",
    parameters: object({ region: { type: 'string', description: "Facultatif : le nom d'une zone (dialogue, formulaire, tableau) à lire seule." } }, []),
  },
  ui_navigate: {
    type: 'function',
    name: 'ui_navigate',
    description: "Va à une entrée du menu de navigation, par son libellé (« Prêts », « Budgets »). Jamais une adresse libre.",
    parameters: object({ destination: { type: 'string', description: "Le libellé de l'entrée du menu." } }, ['destination']),
  },
  ui_click: {
    type: 'function',
    name: 'ui_click',
    description:
      "Clique sur un bouton, un lien, un onglet, une ligne ou une carte de l'écran, par son nom visible. Un geste qui écrit (Enregistrer, Envoyer, Valider, Créer, Produire, Lancer) demande d'abord la confirmation de l'utilisateur ; supprimer, payer, déposer, archiver ou accepter une version se fait à l'écran. Si plusieurs éléments portent ce nom, rien n'est fait : demande lequel.",
    parameters: object(
      {
        name: { type: 'string', description: "Le nom visible de l'élément." },
        role: { type: 'string', enum: CLICK_ROLE_ENUM, description: "Facultatif : le type d'élément, pour départager." },
      },
      ['name']
    ),
  },
  ui_fill: {
    type: 'function',
    name: 'ui_fill',
    description:
      "Remplit un champ de l'écran (texte, nombre, date AAAA-MM-JJ, case à cocher true/false, liste de choix) par son libellé. N'enregistre rien : pour enregistrer, clique ensuite sur le bouton, ce qui demande une confirmation.",
    parameters: object(
      {
        label: { type: 'string', description: 'Le libellé du champ.' },
        value: { type: 'string', description: 'La valeur à mettre (date au format AAAA-MM-JJ, case : true ou false).' },
      },
      ['label', 'value']
    ),
  },
  ui_select: {
    type: 'function',
    name: 'ui_select',
    description: "Choisit une option dans une liste ou un groupe de boutons radio de l'écran, par le libellé du champ puis le nom de l'option.",
    parameters: object(
      {
        label: { type: 'string', description: 'Le libellé de la liste ou du groupe.' },
        option: { type: 'string', description: "Le nom de l'option à choisir." },
      },
      ['label', 'option']
    ),
  },
  ui_scroll: {
    type: 'function',
    name: 'ui_scroll',
    description: "Fait défiler la page : tout en haut (top), tout en bas (bottom), d'un écran vers le bas (down) ou vers le haut (up), ou jusqu'à un titre ou une zone par son nom.",
    parameters: object({ to: { type: 'string', description: "top, bottom, up, down, ou le nom d'un titre ou d'une zone." } }, ['to']),
  },
  ui_back: {
    type: 'function',
    name: 'ui_back',
    description: 'Revient à la page précédente.',
    parameters: object({}, []),
  },
  ui_undo: {
    type: 'function',
    name: 'ui_undo',
    description: "Annule le dernier remplissage fait par Géode sur cette page (jusqu'à dix) et rend la valeur précédente. Un clic déjà joué ne s'annule pas.",
    parameters: object({}, []),
  },
};
